/** Flat key-value translation maps. Payment kiosk only needs de + en. */
export type Lang = 'de' | 'en';

export const DE: Record<string, string> = {
  // Idle screen
  'idle.subtitle': 'HERZLICH WILLKOMMEN',
  'idle.title': 'Parkgebühr bezahlen',
  'idle.badge': '',
  'idle.heroText': 'Kennzeichenerkennung & schrankenlos',
  'idle.selectLang': 'Bitte Sprache wählen / Select Language',
  'idle.tapToStart': 'TOUCH TO START / BERÜHREN',
  'idle.hotline': 'Hotline',
  'idle.cardNotice': 'Kartenzahlung direkt am Terminal',

  // Entry (plate input & autocomplete)
  'entry.title': 'Kennzeichen eingeben',
  'entry.subtitle': 'Geben Sie Ihr Kfz-Kennzeichen über die Tastatur ein (z. B. L340VV)',
  'entry.platePlaceholder': 'L340VV',
  'entry.search': 'SUCHEN ➔',
  'entry.helpText': 'Benötigen Sie Hilfe? Drücken Sie die Hotline-Taste',
  'entry.suggestionsLabel': 'Ist das Ihr Fahrzeug?',

  // Detection confirm/correct
  'detection.title': 'Fahrzeugbestätigung',
  'detection.subtitle': 'Automatische Kamera-Erkennung',
  'detection.camHeader': 'Einfahrtskamera (Kennzeichenerkennung)',
  'detection.captured': 'Erfasst',
  'detection.anprBadge': '📷 ANPR-Erfassung',
  'detection.question': 'Ist dies Ihr Fahrzeug?',
  'detection.confirm': 'JA, WEITER ➔',
  'detection.correct': '✏️ Korrigieren',
  'detection.restart': 'Neustart ↺',

  // Plate Not Found & Recovery
  'notFound.title': 'Kennzeichen nicht gefunden',
  'notFound.subtitle': 'Das eingegebene Kennzeichen konnte nicht direkt zugeordnet werden.',
  'notFound.anprHeader': '📸 Aktuelle Kamera-Erfassungen (Nah-Treffer)',
  'notFound.ocrHeader': '✏️ Kennzeichen manuell korrigieren',
  'notFound.ocrSub': 'Eingabe über die Tastatur anpassen oder neu eingeben:',
  'notFound.editOcr': '✏️ Kennzeichen korrigieren',
  'notFound.selectVehicle': 'Das ist mein Fahrzeug ➔',

  // Fee quote & confirm amount
  'confirm.title': 'Übersicht Parkgebühr',
  'confirm.entryTime': 'Einfahrtszeit:',
  'confirm.exitTime': 'Ausfahrtszeit:',
  'confirm.zone': 'Zone:',
  'confirm.space': 'Parkfläche:',
  'confirm.duration': 'Parkdauer:',
  'confirm.tariff': 'Angewendeter Tarif:',
  'confirm.descriptionFallback': 'Offene Parkgebühr',
  'confirm.amountDue': 'Zu zahlender Betrag',
  'confirm.plateLabel': 'Kennzeichen',

  // Quoting & Arming
  'quoting.status': 'Gebühr wird ermittelt…',
  'arming.status': 'Kartenleser wird vorbereitet…',
  // Shown only when the terminal has not confirmed the command after a couple of seconds.
  // Deliberately reassuring rather than alarming: at this point nothing has gone wrong yet.
  'arming.slow': 'Verbindung zum Kartenleser wird hergestellt…',

  // Tap card
  'tap.title': 'Bitte Karte an das Terminal halten',
  'tap.status': 'Contactless Payment Active — EC-Karte / Credit Card / Smartphone',

  // Success
  'success.title': 'Zahlung erfolgreich!',
  'success.subtitle': 'Gute Fahrt! Ihre Ausfahrt ist freigeschaltet.',
  'success.receiptTitle': 'Digitaler Parkbeleg / Receipt',
  'success.receiptSub': 'Scannen Sie den QR-Code mit Ihrem Smartphone, um den Beleg abzurufen.',
  'success.receipt': 'Scannen Sie den Code für Ihre Quittung:',
  'success.generating': 'Quittung wird erstellt…',
  'success.autoReset': 'Automatischer Neustart in',
  'success.seconds': 'Sekunden...',

  // Nothing due — one wording per reason. Covered is never "paid": nobody charged the parker.
  'nothingDue.paid.title': 'Bereits bezahlt',
  'nothingDue.paid.subtitle': 'Dieser Parkvorgang ist bezahlt. Gute Fahrt!',
  'nothingDue.covered.title': 'Durch Ihr Parkrecht gedeckt',
  'nothingDue.covered.subtitle': 'Für diesen Parkvorgang ist nichts zu bezahlen. Gute Fahrt!',
  'nothingDue.free.title': 'Nichts zu bezahlen',
  'nothingDue.free.subtitle': 'Für diesen Parkvorgang fällt keine Gebühr an. Gute Fahrt!',

  // Failed
  'failed.title': 'Zahlung nicht abgeschlossen',

  // Terminal unreachable — the command never reached the reader. Deliberately NOT phrased as a
  // payment failure: nothing was wrong with the card, and saying so sends people away convinced
  // their card was refused.
  'unreachable.title': 'Automat antwortet nicht',
  'unreachable.body': 'Ihre Karte wurde nicht belastet. Bitte erneut versuchen oder am Ausgang mit dem QR-Code bezahlen.',

  // Generic actions
  'action.cancel': 'Abbrechen ✕',
  'action.pay': 'Mit Karte zahlen',
  'action.payCoins': 'Mit Münzen zahlen',
  'action.payCredit': 'Mit Guthaben bezahlen',
  'confirm.creditAvailable': 'Guthaben verfügbar',
  'credit.redeeming': 'Guthaben wird verwendet…',
  'credit.used': 'Guthaben verwendet',
  'credit.failed': 'Guthaben konnte nicht verwendet werden. Bitte mit Karte oder Münzen zahlen.',
  'coins.starting': 'Münzprüfer wird gestartet…',
  'coins.title': 'Bitte Münzen einwerfen',
  'coins.remaining': 'Noch zu zahlen',
  'coins.inserted': 'Eingeworfen',
  'coins.noChange': 'Kein Wechselgeld – Überzahlung wird als Guthaben gespeichert.',
  'coins.restByCard': 'Rest mit Karte',
  'coins.counting': 'Münzen werden gezählt…',
  'coins.creditedTitle': 'Guthaben gespeichert',
  'coins.creditedSub': 'Die Parkgebühr ist noch offen.',
  'coins.creditedAmount': 'Als Guthaben gespeichert',
  'coins.fault': 'Münzprüfer gestört.',
  'coins.savedAsCredit': 'wurden als Guthaben gespeichert.',
  'coins.unavailable': 'Münzzahlung derzeit nicht möglich. Bitte mit Karte zahlen.',
  'coins.notReported': 'Die Münzen konnten nicht gezählt werden. Eingeworfene Beträge werden als Guthaben gespeichert.',
  'success.creditSaved': 'Überzahlung als Guthaben gespeichert',
  'success.paidWithCredit': 'Mit Guthaben bezahlt',
  'footer.coins': 'Münzen',
  'action.done': 'Fertig ✓',
  'action.retry': 'Erneut versuchen',
  'action.restart': 'Neustart ↺',

  // Errors
  'error.service': 'Zahlungsdienst nicht erreichbar. Bitte versuchen Sie es erneut.',
  'error.alreadyPaid': 'Dieses Kennzeichen ist bereits bezahlt.',
  'error.noFee': 'Keine Gebühr für dieses Kennzeichen.',
  'error.armFailed': 'Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
  'error.priceChanged': 'Die Parkgebühr hat sich inzwischen geändert. Bitte bestätigen Sie den neuen Betrag.',

  // Payment outcome reasons
  'outcome.Aborted': 'Zahlung abgebrochen.',
  'outcome.TimedOut': 'Karte wurde nicht rechtzeitig vorgelegt.',
  'outcome.Failed': 'Zahlung fehlgeschlagen.',
  'outcome.default': 'Zahlung wurde nicht abgeschlossen.',

  // ─────────────────── Settlement page (/pay/:id) — the parker's own phone, not the column.
  // German wording follows the old platform (Parkgebühr / Einfahrt / Ausfahrt / Kennzeichen).
  'pay.loading': 'Parkgebühr wird ermittelt…',
  'pay.title': 'Parkgebühr bezahlen',
  'pay.entry': 'Einfahrt',
  'pay.exit': 'Ausfahrt',
  'pay.duration': 'Parkdauer',
  'pay.zone': 'Zone',
  'pay.space': 'Parkfläche',
  'pay.gross': 'Parkgebühr gesamt',
  'pay.alreadyPaid': 'Bereits bezahlt',
  'pay.amountDue': 'Offener Betrag',
  'pay.frozenNote': 'Dieser Betrag ist fixiert und ändert sich nicht mehr.',
  'pay.payNow': 'Jetzt bezahlen',
  'pay.securedBy': 'Sichere Zahlung über Stripe. Kreditkarte, Apple Pay und Google Pay.',
  'pay.cancelled': 'Die Zahlung wurde abgebrochen. Es wurde nichts abgebucht.',
  'pay.redirecting': 'Sie werden zur Zahlung weitergeleitet…',
  'pay.retry': 'Erneut versuchen',

  // Waiting for the payment to be confirmed server-side
  'pay.confirming.title': 'Zahlung wird bestätigt',
  'pay.confirming.text': 'Einen Moment bitte – wir warten auf die Bestätigung Ihrer Zahlung.',
  'pay.pending.title': 'Noch keine Bestätigung',
  'pay.pending.text':
    'Ihre Zahlung ist bei uns noch nicht eingelangt. Das kann kurz dauern. Bitte zahlen Sie nicht erneut – prüfen Sie stattdessen nochmals.',
  'pay.pending.recheck': 'Erneut prüfen',

  // Paid
  'pay.paid.title': 'Zahlung erfolgreich',
  'pay.paid.text': 'Vielen Dank. Ihre Parkgebühr ist beglichen.',
  'pay.receipt.title': 'Beleg',
  'pay.receipt.titlePayment': 'Zahlungsbestätigung',
  'pay.receipt.open': 'Beleg öffnen',
  'pay.receipt.openPayment': 'Zahlungsbestätigung öffnen',
  'pay.receipt.generating': 'Beleg wird erstellt…',
  'pay.receipt.failed': 'Der Beleg konnte nicht erstellt werden. Ihre Zahlung ist trotzdem gebucht.',
  'pay.receipt.unavailable': 'Bei Online-Zahlungen wird kein Parkbeleg ausgestellt. Ihre Zahlung ist gebucht.',
  'pay.receipt.opened': 'Beleg wurde bereits geöffnet.',

  // Nothing to pay — each reason gets its own message
  'pay.done.alreadyPaid.title': 'Bereits bezahlt',
  'pay.done.alreadyPaid.text': 'Für diesen Parkvorgang ist kein Betrag mehr offen.',
  'pay.done.covered.title': 'Nichts zu bezahlen',
  'pay.done.covered.text': 'Dieser Parkvorgang ist durch Ihr Parkrecht gedeckt.',
  'pay.done.free.title': 'Kostenfrei',
  'pay.done.free.text': 'Für diesen Parkvorgang fällt keine Gebühr an.',
  'pay.done.notPriceable.title': 'Keine Zahlung möglich',
  'pay.done.notPriceable.text':
    'Für diesen Parkvorgang liegt kein Tarif vor.',

  // Errors
  'pay.error.title': 'Etwas ist schiefgelaufen',
  'pay.error.load': 'Die Parkgebühr konnte nicht ermittelt werden. Bitte versuchen Sie es erneut.',
  'pay.error.start': 'Die Zahlung konnte nicht gestartet werden. Bitte versuchen Sie es erneut.',
  'pay.error.notFound': 'Zu diesem Link wurde kein Parkvorgang gefunden.',
  'pay.error.badLink': 'Dieser Link ist unvollständig. Bitte scannen Sie den QR-Code erneut.',
  'pay.error.tooMany': 'Zu viele Anfragen. Bitte warten Sie einen Moment.',
  'pay.error.noCheckoutUrl': 'Die Zahlungsseite ist derzeit nicht erreichbar. Bitte versuchen Sie es erneut.',
  'pay.error.amountChanged': 'Der Betrag hat sich geändert. Bitte prüfen Sie den neuen Betrag.',
  'pay.error.redirectBlocked': 'Diese Zahlungsseite wurde nicht über den offiziellen QR-Code geöffnet. Bitte scannen Sie den QR-Code erneut.',
  'pay.back': 'Zurück',
  'pay.returnToApp': 'Zurück zur App',
  'pay.autoRedirect': 'Weiterleitung zur App in',
  'pay.seconds': 'Sekunden…',
};

export const EN: Record<string, string> = {
  // Idle screen
  'idle.subtitle': 'WELCOME',
  'idle.title': 'Pay Parking Fee',
  'idle.badge': '',
  'idle.heroText': 'Barrier-Free Automatic Plate Recognition',
  'idle.selectLang': 'Select Language / Bitte Sprache wählen',
  'idle.tapToStart': 'TOUCH TO START ➔',
  'idle.hotline': 'Hotline',
  'idle.cardNotice': 'Card payment direct at terminal',

  // Entry (plate input & autocomplete)
  'entry.title': 'Enter Licence Plate',
  'entry.subtitle': 'Type your vehicle licence plate using the keypad below (e.g. L340VV)',
  'entry.platePlaceholder': 'L340VV',
  'entry.search': 'SEARCH ➔',
  'entry.helpText': 'Need help? Use the hotline information above',
  'entry.suggestionsLabel': 'Is this your vehicle?',

  // Detection confirm/correct
  'detection.title': 'Vehicle Confirmation',
  'detection.subtitle': 'Automatic Camera Detection',
  'detection.camHeader': 'Entrance Camera (Plate Recognition)',
  'detection.captured': 'Captured',
  'detection.anprBadge': '📷 ANPR Capture',
  'detection.question': 'Is this your vehicle?',
  'detection.confirm': 'YES, PROCEED ➔',
  'detection.correct': '✏️ Correct Plate',
  'detection.restart': 'Restart ↺',

  // Plate Not Found & Recovery
  'notFound.title': 'Licence Plate Not Found',
  'notFound.subtitle': 'The entered plate could not be directly matched with an active session.',
  'notFound.anprHeader': '📸 Recent Camera Captures (Near Matches)',
  'notFound.ocrHeader': '✏️ Manually Correct Licence Plate',
  'notFound.ocrSub': 'Edit your plate on the keypad or re-enter:',
  'notFound.editOcr': '✏️ Correct Licence Plate',
  'notFound.selectVehicle': 'This is my vehicle ➔',

  // Fee quote & confirm amount
  'confirm.title': 'Parking Fee Summary',
  'confirm.entryTime': 'Entry Time:',
  'confirm.exitTime': 'Exit Time:',
  'confirm.zone': 'Zone:',
  'confirm.space': 'Parking Space:',
  'confirm.duration': 'Duration:',
  'confirm.tariff': 'Applied Tariff:',
  'confirm.descriptionFallback': 'Open Parking Fee',
  'confirm.amountDue': 'Amount to Pay',
  'confirm.plateLabel': 'Licence Plate',

  // Quoting & Arming
  'quoting.status': 'Calculating parking fee…',
  'arming.status': 'Preparing card reader…',
  'arming.slow': 'Connecting to the card reader…',

  // Tap card
  'tap.title': 'Please tap your card on the reader',
  'tap.status': 'Contactless Payment Active — Credit Card / Debit / Smartphone',

  // Success
  'success.title': 'Payment Successful!',
  'success.subtitle': 'Have a safe trip! Your exit has been authorized.',
  'success.receiptTitle': 'Digital Parking Receipt',
  'success.receiptSub': 'Scan the QR code with your smartphone to download your receipt.',
  'success.receipt': 'Scan for your receipt:',
  'success.generating': 'Generating receipt…',
  'success.autoReset': 'Automatic reset in',
  'success.seconds': 'seconds...',

  // Nothing due — one wording per reason. Covered is never "paid": nobody charged the parker.
  'nothingDue.paid.title': 'Already paid',
  'nothingDue.paid.subtitle': 'This parking session is paid. Have a safe trip!',
  'nothingDue.covered.title': 'Covered by your parking right',
  'nothingDue.covered.subtitle': 'There is nothing to pay for this parking session. Have a safe trip!',
  'nothingDue.free.title': 'Nothing to pay',
  'nothingDue.free.subtitle': 'There is no fee for this parking session. Have a safe trip!',

  // Failed
  'failed.title': 'Payment Not Completed',

  // Terminal unreachable — see the German block: this is not a declined card and must not read
  // like one.
  'unreachable.title': 'Terminal Not Responding',
  'unreachable.body': 'Your card was not charged. Please try again, or pay with the QR code at the exit.',

  // Generic actions
  'action.cancel': 'Cancel ✕',
  'action.pay': 'Pay by Card',
  'action.payCoins': 'Pay with coins',
  'action.payCredit': 'Pay with credit',
  'confirm.creditAvailable': 'Credit available',
  'credit.redeeming': 'Using your credit…',
  'credit.used': 'Credit used',
  'credit.failed': 'Your credit could not be used. Please pay by card or coins.',
  'coins.starting': 'Starting the coin acceptor…',
  'coins.title': 'Please insert coins',
  'coins.remaining': 'Still to pay',
  'coins.inserted': 'Inserted',
  'coins.noChange': 'No change given – overpayment is saved as credit.',
  'coins.restByCard': 'Rest by card',
  'coins.counting': 'Counting coins…',
  'coins.creditedTitle': 'Credit saved',
  'coins.creditedSub': 'The parking fee is still open.',
  'coins.creditedAmount': 'Saved as credit',
  'coins.fault': 'Coin acceptor out of order.',
  'coins.savedAsCredit': 'saved as credit.',
  'coins.unavailable': 'Coin payment is not available right now. Please pay by card.',
  'coins.notReported': 'The coins could not be counted. Any amount inserted is saved as credit.',
  'success.creditSaved': 'Overpayment saved as credit',
  'success.paidWithCredit': 'Paid with credit',
  'footer.coins': 'Coins',
  'action.done': 'Done ✓',
  'action.retry': 'Try Again',
  'action.restart': 'Restart ↺',

  // Errors
  'error.service': 'Payment service unavailable. Please try again.',
  'error.alreadyPaid': 'This plate is already paid.',
  'error.noFee': 'No open fee for this plate.',
  'error.armFailed': 'Could not start payment. Please try again.',
  'error.priceChanged': 'The parking fee has changed since it was shown. Please confirm the new amount.',

  // Payment outcome reasons
  'outcome.Aborted': 'Payment cancelled.',
  'outcome.TimedOut': 'Card presented timed out.',
  'outcome.Failed': 'Payment failed.',
  'outcome.default': 'Payment was not completed.',

  // ─────────────────── Settlement page (/pay/:id) — the parker's own phone, not the column.
  'pay.loading': 'Calculating your parking fee…',
  'pay.title': 'Pay your parking fee',
  'pay.entry': 'Entry',
  'pay.exit': 'Exit',
  'pay.duration': 'Duration',
  'pay.zone': 'Zone',
  'pay.space': 'Parking space',
  'pay.gross': 'Total parking fee',
  'pay.alreadyPaid': 'Already paid',
  'pay.amountDue': 'Amount due',
  'pay.frozenNote': 'This amount is fixed and will not change.',
  'pay.payNow': 'Pay now',
  'pay.securedBy': 'Secure payment via Stripe. Card, Apple Pay and Google Pay.',
  'pay.cancelled': 'The payment was cancelled. Nothing has been charged.',
  'pay.redirecting': 'Taking you to the payment page…',
  'pay.retry': 'Try again',

  // Waiting for the payment to be confirmed server-side
  'pay.confirming.title': 'Confirming your payment',
  'pay.confirming.text': 'One moment — we are waiting for your payment to be confirmed.',
  'pay.pending.title': 'Not confirmed yet',
  'pay.pending.text':
    'Your payment has not reached us yet. This can take a moment. Please do not pay again — check once more instead.',
  'pay.pending.recheck': 'Check again',

  // Paid
  'pay.paid.title': 'Payment successful',
  'pay.paid.text': 'Thank you. Your parking fee has been settled.',
  'pay.receipt.title': 'Receipt',
  'pay.receipt.titlePayment': 'Payment confirmation',
  'pay.receipt.open': 'Open receipt',
  'pay.receipt.openPayment': 'Open payment confirmation',
  'pay.receipt.generating': 'Generating your receipt…',
  'pay.receipt.failed': 'The receipt could not be generated. Your payment went through all the same.',
  'pay.receipt.unavailable': 'Online payments do not issue a parking receipt. Your payment has been recorded.',
  'pay.receipt.opened': 'Receipt already opened.',

  // Nothing to pay — each reason gets its own message
  'pay.done.alreadyPaid.title': 'Already paid',
  'pay.done.alreadyPaid.text': 'There is nothing left to pay for this stay.',
  'pay.done.covered.title': 'Nothing to pay',
  'pay.done.covered.text': 'This stay is covered by your parking right.',
  'pay.done.free.title': 'Free of charge',
  'pay.done.free.text': 'There is no fee for this stay.',
  'pay.done.notPriceable.title': 'Payment not possible',
  'pay.done.notPriceable.text':
    'No tariff is available for this stay.',

  // Errors
  'pay.error.title': 'Something went wrong',
  'pay.error.load': 'We could not calculate your parking fee. Please try again.',
  'pay.error.start': 'We could not start the payment. Please try again.',
  'pay.error.notFound': 'No parking stay was found for this link.',
  'pay.error.badLink': 'This link is incomplete. Please scan the QR code again.',
  'pay.error.tooMany': 'Too many requests. Please wait a moment.',
  'pay.error.noCheckoutUrl': 'The payment page is unavailable right now. Please try again.',
  'pay.error.amountChanged': 'The amount has changed. Please check the new amount.',
  'pay.error.redirectBlocked': 'This payment page was not opened from the official QR code. Please scan the QR code again.',
  'pay.back': 'Back',
  'pay.returnToApp': 'Back to Parker App',
  'pay.autoRedirect': 'Redirecting to app in',
  'pay.seconds': 'seconds…',
};

export function t(lang: Lang, key: string): string {
  const map = lang === 'de' ? DE : EN;
  return map[key] ?? key;
}
